import { useState } from 'react';
import { Label } from './label';
import { Upload, X, User, Building2, ImageIcon, Pencil } from 'lucide-react';
import { Button } from './button';
import { toast } from 'sonner';
import ImageEditModal from './ImageEditModal';

const MAX_UPLOAD_MB = {
  avatar: 8,
  profile: 15,
  headshot: 15,
  gallery: 15,
  logo: 8,
  cover: 10,
  banner: 10,
  'news-cover': 15,
  flyer: 15,
  event: 15,
  messenger: 10,
  free: 15,
};

/**
 * SingleImageUpload — polished image tile that delegates upload/crop work to
 * ImageEditModal. Touch devices keep edit/remove actions visible because there
 * is no dependable hover state on iPhone, iPad or Android.
 */
export default function SingleImageUpload({
  value,
  onChange,
  label = 'Image',
  description = 'Upload an image',
  type = 'profile',
  className = '',
  recommendedSize = '',
  hideRecommendation = false,
}) {
  const [open, setOpen] = useState(false);

  const handleRemove = () => {
    onChange('');
    toast.success('Image removed');
  };

  const getPlaceholderIcon = () => {
    switch (type) {
      case 'logo':
        return <Building2 className="h-7 w-7 text-theme-muted" />;
      case 'cover':
        return <ImageIcon className="h-7 w-7 text-theme-muted" />;
      default:
        return <User className="h-7 w-7 text-theme-muted" />;
    }
  };

  const aspect = type === 'cover'
    ? 'aspect-[3/1]'
    : type === 'event' || type === 'news-cover'
      ? 'aspect-video'
      : type === 'flyer'
        ? 'aspect-[4/5]'
        : 'aspect-square';
  const size = type === 'cover' || type === 'event' || type === 'news-cover'
    ? 'w-full max-w-md'
    : type === 'flyer'
      ? 'w-full max-w-[200px]'
      : 'w-full max-w-[160px]';

  const defaultRecommended = (() => {
    switch (type) {
      case 'cover': return '1500 × 500 px';
      case 'logo': return 'up to 1600 × 1600 px (transparent backgrounds supported)';
      case 'news-cover': return '1600 × 900 px';
      case 'avatar': return '400 × 400 px';
      case 'gallery': return '1080 × 1350 px';
      case 'flyer': return '1080 × 1350 px (portrait — full image preserved)';
      case 'event': return '1600 × 900 px (landscape / 16:9)';
      default: return '800 × 800 px';
    }
  })();
  const sizeLabel = recommendedSize || defaultRecommended;
  const maxUploadMb = MAX_UPLOAD_MB[type] || 15;

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <Label className="text-theme-text font-medium text-sm">{label}</Label>}
      {description && <p className="text-[11px] text-theme-muted -mt-1">{description}</p>}
      {!hideRecommendation && (
        <p className="text-[11px] text-theme-muted -mt-1" data-testid={`single-image-recommended-${type}`}>
          Recommended: {sizeLabel} · JPG/PNG/WEBP/HEIC, up to {maxUploadMb}MB
        </p>
      )}

      <div
        className={`relative ${size} ${aspect} rounded-xl overflow-hidden border border-theme bg-theme-surface/50 shadow-elev-1 group transition-all hover:shadow-elev-2 hover:border-[#002FA7]/40`}
        data-testid={`single-image-tile-${type}`}
      >
        {value ? (
          <>
            <img
              src={value}
              alt={label || 'Image'}
              className={`w-full h-full ${type === 'flyer' ? 'object-contain bg-theme' : 'object-cover'}`}
            />
            <div
              className="absolute inset-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-2.5 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100"
              data-testid={`single-image-actions-${type}`}
            >
              <Button
                type="button"
                size="sm"
                onClick={() => setOpen(true)}
                className="min-h-11 touch-manipulation bg-white/95 px-3 text-xs text-[#0B0C0E] shadow-elev-1 hover:bg-white sm:min-h-8"
                data-testid={`single-image-edit-${type}`}
              >
                <Pencil className="mr-1 h-3.5 w-3.5" />
                Edit
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleRemove}
                className="h-11 w-11 touch-manipulation bg-rose-500/95 p-0 text-white shadow-elev-1 hover:bg-rose-500 sm:h-8 sm:w-8"
                aria-label="Remove"
                data-testid={`single-image-remove-${type}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full h-full min-h-11 touch-manipulation flex flex-col items-center justify-center gap-2 text-theme-muted hover:text-theme-text transition-colors"
            data-testid={`single-image-add-${type}`}
          >
            {getPlaceholderIcon()}
            <span className="text-xs font-medium inline-flex items-center gap-1.5">
              <Upload className="h-3.5 w-3.5" />
              Upload
            </span>
          </button>
        )}
      </div>

      <ImageEditModal
        open={open}
        onOpenChange={setOpen}
        initialUrl={value || null}
        type={type}
        title={value ? `Edit ${label || 'image'}` : `Upload ${label || 'image'}`}
        description={value ? 'Reposition, zoom, or replace.' : 'Pick a file, then crop and save.'}
        onSaved={(url) => onChange(url)}
      />
    </div>
  );
}
